import { useEffect, useMemo, useState } from "react";

import { useGnarlyGoatsContracts } from "./useGnarlyGoatsContracts";

export interface Metadata {
	initSeqIndex?: number // the edition
	randSeqIndex: number
	tokenID: number
	imageHash: string
	imageCID: string
}

export function useProvenance() {
	const { contract } = useGnarlyGoatsContracts()

	// starting index from contract
	const [startingIndex, setStartingIndex] = useState<number>()
	// provenance hash from contract
	const [provenanceHash, setProvenanceHash] = useState<string>()
	// parsed metadata stored in the /public folder
	const [metadata, setMetadata] = useState<Metadata[]>()

	/**
	 * Initialize Starting Index & Provenance Hash
	 */
	useEffect(() => {
		const init = async () => {
			try {
				const _startingIndex = await contract?.startingIndex()
				setStartingIndex(_startingIndex.toNumber())
				const _provenanceHash = await contract?.provenance()
				setProvenanceHash(_provenanceHash)
			} catch (err) {
				console.log('Error retrieving starting index', err)
			}
		}
		init()
	}, [contract])

	/**
	 * Initialize Metadata
	 */
	useMemo(() => {
		const init = async () => {
			// parse the provenance metada json file from local constants
			if (startingIndex == null) {
				return
			}

			/**
			 * Fetch the metadata from Gnarly Goats Piñata IPFS Gateway
			 */
			let IPFS_CID =
				'bafkreibtfw2npj5uamng6uz7l55olq4yazyvzdwhzp73ynmdfktfivcgne'

			let response = await fetch(
				`https://ipfs.gnarlygoats.com/ipfs/${IPFS_CID}`
			)

			let responseAsMetadata = (await response.json()) as Metadata[]

			let metadata = responseAsMetadata
				.map((m) => {
					const meta = m as Metadata
					meta.tokenID = meta.randSeqIndex + startingIndex
					if (!meta.imageCID) meta.imageCID = 'HIDDEN'
					return {
						randSeqIndex: meta.randSeqIndex,
						tokenID: meta.randSeqIndex + startingIndex,
						imageHash: meta.imageHash,
						imageCID: meta.imageCID ?? 'HIDDEN',
					}
				})
				.sort(
					(a: Metadata, b: Metadata) =>
						Number(a.randSeqIndex) - Number(b.randSeqIndex)
				)

			setMetadata(metadata)
		}
		init()
	}, [startingIndex])

	/**
	 * Generate concatenated image hash
	 */
	const concatImageHashes = useMemo(() => {
		if (metadata != null && metadata.length > 0) {
			return metadata
				.map((m) => {
					return m.imageHash
				})
				.reduce((a, b) => {
					return a + b
				}, '')
		} else return null
	}, [metadata])

	const loading = useMemo(() => {
		return (
			startingIndex == null ||
			provenanceHash == null ||
			metadata == null ||
			concatImageHashes == null
		)
	}, [startingIndex, provenanceHash, metadata, concatImageHashes])

	return { startingIndex, provenanceHash, metadata, concatImageHashes, loading }
}
